import type {
  PrintRouteSchema,
  PrintRouteSchemaWithId,
} from "@repo/validators";
import type { z } from "zod";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PrintRoute } from "~/providers/printing-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { usePrintRouteStore } from "~/stores/use-print-route-store";
import { usePermissionManager } from "./use-permission-manager";

export const usePrintRouteManager = () => {
  const { t } = useTranslation("common");

  const { setPrintRouteDialogOpen, setSelectedPrintRoute } =
    usePrintRouteStore();

  const { hasPermission } = usePermissionManager();

  const { mutate: createPrintRoute } =
    api.printer.createPrintRoute.useMutation();

  const { mutate: updatePrintRoute } =
    api.printer.updatePrintRoute.useMutation();

  const createPrintRouteHandler = async (
    printRoute: z.infer<ReturnType<typeof PrintRouteSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_print_route,
      label: t("Permissions.create_print_route_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PrintRouteSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      createPrintRoute(printRoute, {
        onSuccess() {
          toast.success(
            capitalize(
              t("create_item_success", { item: t("PrintRoute.print_route") }),
            ),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          toast.error(
            capitalize(
              t("create_item_error", { item: t("PrintRoute.print_route") }),
            ),
          );
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("PrintRoute.print_route"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updatePrintRouteHandler = async (
    printRoute: z.infer<ReturnType<typeof PrintRouteSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_print_route,
      label: t("Permissions.create_print_route_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PrintRouteSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      updatePrintRoute(printRoute, {
        onSuccess() {
          toast.success(
            capitalize(
              t("update_item_success", { item: t("PrintRoute.print_route") }),
            ),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          toast.error(
            capitalize(
              t("update_item_error", { item: t("PrintRoute.print_route") }),
            ),
          );
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", {
                  item: t("PrintRoute.print_route"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const openPrintRouteDialog = async (printRoute?: PrintRoute) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_print_route,
      label: t("Permissions.create_print_route_label"),
    });

    if (!permission) return false;

    setSelectedPrintRoute(printRoute);
    setPrintRouteDialogOpen(true);
  };
  return {
    openPrintRouteDialog,
    createPrintRoute: createPrintRouteHandler,
    updatePrintRoute: updatePrintRouteHandler,
  };
};
